/* FDC 2.0 - visibility-aware lazy background video loader.
   Used by landing pages that ship desktop and mobile encodes of the same clip
   side by side. Only the variant that is actually visible at the current
   viewport ever downloads, and only once it is near the viewport.
   Without this, a phone downloads the desktop clip too (hidden by CSS is not
   hidden from the network), which is the single biggest mobile weight item on
   these pages. Transform/opacity free, no layout work: each video carries an
   aspect-ratio inline style so nothing shifts before metadata arrives. */
(function () {
  'use strict';

  var SEL = 'video.ab-lazyvid';

  function saveData() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    return /(^|-)2g$/.test(c.effectiveType || '');
  }

  function visible(v) {
    // offsetParent is null for display:none ancestors, which is exactly how the
    // desktop/mobile variants are toggled (Bootstrap d-none / d-md-block).
    return v.offsetParent !== null || getComputedStyle(v).display !== 'none';
  }

  function attach(v) {
    if (v.dataset.abLoaded === '1') return;
    var src = v.querySelector('source[data-src]');
    if (!src) return;
    v.dataset.abLoaded = '1';
    src.src = src.getAttribute('data-src');
    // iOS refuses inline autoplay unless both of these are set on the element.
    v.muted = true;
    v.setAttribute('muted', '');
    v.playsInline = true;
    // af124 (family QA 10/2, real iPhones): a lone play() after load() can be rejected or stall on iOS, leaving the
    // clip frozen until a refresh. Live ships these videos with the autoplay attribute, so WebKit owns playback
    // (and resumes it when the clip scrolls back into view). Same here, plus a retry once data arrives.
    v.autoplay = true;
    v.setAttribute('autoplay', '');
    v.load();
    kick(v);
    v.addEventListener('canplay', function () { kick(v); });
    watch(v);
  }

  // af125 (behavior diff vs live, real iPhone 15): iOS can report a clip as playing (paused=false, readyState 4)
  // while currentTime sits at 0, so nothing above re-kicks it and it looks frozen until a refresh. Live never hits
  // this because its clips are plain autoplay. Watchdog: an on-screen clip that claims to play but has not advanced
  // for ~1.2s gets pause()+play(); if that does not move it, a reload+play. Stops after 6 recoveries per clip.
  function watch(v) {
    var last = -1, still = 0, fixes = 0;
    var id = setInterval(function () {
      if (fixes >= 6) { clearInterval(id); return; }
      if (document.hidden || v.paused || v.readyState < 2 || !visible(v)) { still = 0; last = -1; return; }
      var r = v.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= window.innerHeight) { still = 0; last = -1; return; }
      var t = v.currentTime;
      if (last >= 0 && Math.abs(t - last) < 0.05) still++; else still = 0;
      last = t;
      if (still < 3) return;
      still = 0; last = -1; fixes++;
      if (fixes % 2) { v.pause(); kick(v); }
      else { v.load(); kick(v); }
    }, 400);
  }

  function kick(v) {
    if (!v.paused) return;
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function boot() {
    var vids = Array.prototype.slice.call(document.querySelectorAll(SEL));
    if (!vids.length) return;

    if (saveData() || !('IntersectionObserver' in window)) {
      // No observer support: load the visible ones immediately rather than
      // leaving a permanently blank box in front of a customer.
      if (!saveData()) vids.filter(visible).forEach(attach);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        if (!visible(e.target)) return; // wrong breakpoint variant, skip
        // keep observing: a loaded clip that is paused when it comes back into view is restarted
        if (e.target.dataset.abLoaded === '1') { kick(e.target); return; }
        attach(e.target);
      });
    }, { rootMargin: '200px 0px' });

    vids.forEach(function (v) { io.observe(v); });

    // af124: belt and braces for iOS. Any touch or scroll re-kicks loaded clips that are on screen but paused
    // (a stalled first play() otherwise needed a refresh). A user gesture also lifts autoplay throttling.
    var kt = 0;
    function rekick() {
      var now = Date.now();
      if (now - kt < 400) return;
      kt = now;
      vids.forEach(function (v) {
        if (v.dataset.abLoaded !== '1' || !v.paused || !visible(v)) return;
        var r = v.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight) kick(v);
      });
    }
    document.addEventListener('touchstart', rekick, { passive: true });
    // touchend/click are real user activations on iOS: a play() inside them is allowed even when autoplay is
    // blocked (Low Power Mode), so tapping anywhere starts the visible clips (we hide the native play glyph).
    document.addEventListener('touchend', function () { kt = 0; rekick(); }, { passive: true });
    document.addEventListener('click', function () { kt = 0; rekick(); }, { passive: true });
    window.addEventListener('scroll', rekick, { passive: true });
    setTimeout(rekick, 2500);

    // Breakpoint can change after load (rotation, resize). Any variant that has
    // become visible and is in view gets picked up here.
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        vids.forEach(function (v) {
          if (v.dataset.abLoaded === '1' || !visible(v)) return;
          var r = v.getBoundingClientRect();
          if (r.top < window.innerHeight + 200 && r.bottom > -200) attach(v);
        });
      }, 200);
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
